import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Truck, Heart, Globe2, Handshake, Beaker, Feather } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import PawsHarmonyCard from "../components/PawsHarmonyCard";
import { RecipeCard } from "./Recipes";
import usePageMeta from "../hooks/usePageMeta";

const HERO_IMG = "/brand/home-hero-studio.png";

const CATEGORIES = [
  {
    id: "essential-oils",
    name: "Essential Oils",
    blurb: "Singles & artisan blends",
    image: "https://images.unsplash.com/photo-1608571424634-58ae03e6edcf?crop=entropy&cs=srgb&fm=jpg&w=940&q=85",
  },
  {
    id: "diy-supplies",
    name: "DIY Supplies",
    blurb: "Candles, soaps & rollers",
    image: "https://images.pexels.com/photos/34241258/pexels-photo-34241258.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "garden-gifts",
    name: "Home, Garden & Gifts",
    blurb: "Statues, planters, birdhouses & home accents",
    image: "https://images.pexels.com/photos/11213196/pexels-photo-11213196.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  usePageMeta({
    title: "DC Gift World Too — Pure Essential Oils, DIY Supplies & Garden Gifts",
    description: "Hand-curated apothecary of pure essential oils (Simply Earth Ambassador), DIY candle & soap supplies, and storybook garden gifts. Free shipping over $75. Made with love in Limon, CO.",
  });

  const [newArrivals, setNewArrivals] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/products", { params: { featured: true } }),
      api.get("/recipes", { params: { featured: true } }),
      api.get("/products"),
    ])
      .then(([p, r, all]) => {
        setFeatured(p.data.slice(0, 8));
        setRecipes(r.data.slice(0, 3));
        const sorted = [...all.data].sort((a, b) =>
          (b.created_at || "").localeCompare(a.created_at || "")
        );
        setNewArrivals(sorted.slice(0, 4));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative">
        <div className="grid lg:grid-cols-12 min-h-[78vh]">
          <div className="lg:col-span-6 flex items-center px-5 sm:px-10 lg:px-16 py-16 lg:py-24 order-2 lg:order-1">
            <div className="max-w-xl">
              <p className="eyebrow mb-5" data-testid="hero-eyebrow">A Curated Apothecary</p>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] text-[var(--ink-forest)] tracking-tight">
                Nature&apos;s craft
                <span className="italic text-[var(--terracotta)]"> & comfort</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-[var(--muted-fg)] leading-relaxed max-w-md">
                Hand-poured oils, heirloom DIY supplies, and storybook garden friends — gathered for
                the kind of home that smells like cinnamon and sounds like wind in the trees.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to="/shop"
                  className="inline-flex items-center gap-2 bg-[var(--terracotta)] text-white rounded-full px-8 py-4 text-sm font-semibold hover:bg-[var(--terracotta-deep)] transition-colors"
                  data-testid="hero-shop-cta"
                >
                  Shop the collection
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/shop?category=essential-oils"
                  className="inline-flex items-center gap-2 border border-[var(--ink-forest)]/20 text-[var(--ink-forest)] rounded-full px-8 py-4 text-sm font-medium hover:bg-[var(--ink-forest)] hover:text-white transition-all"
                  data-testid="hero-oils-cta"
                >
                  Explore the oils
                </Link>
              </div>

              <div className="mt-14 grid grid-cols-3 gap-4 max-w-md">
                <Stat icon={Sparkles} label="Hand-curated" value="Always" />
                <Stat icon={Truck} label="Free shipping" value="Over $75" />
                <Stat icon={Heart} label="Made with" value="Love" />
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 relative order-1 lg:order-2 min-h-[50vh]">
            <img
              src={HERO_IMG}
              alt="A cozy Limon, Colorado apothecary studio at golden hour with amber bottles, hanging herbs, and a sleeping cat and dog"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Soft bottom vignette so the welcome card stays legible without muddying the painting */}
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[var(--ink-forest)]/25 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 lg:bottom-10 lg:left-10 lg:right-10">
              <div
                className="bg-[var(--bg-cream)] rounded-2xl p-5 border border-[var(--border)] shadow-2xl max-w-sm ring-1 ring-[var(--gold)]/20"
                data-testid="home-welcome-card"
              >
                <p className="eyebrow mb-1.5 flex items-center gap-2 text-[var(--terracotta)]">
                  <span className="w-2 h-2 rounded-full bg-[var(--terracotta)] animate-pulse" aria-hidden />
                  Shop is open · Orders ship in 1–3 days
                </p>
                <p className="font-serif text-xl text-[var(--ink-forest)] leading-snug">
                  <span className="italic">Happy you&apos;re here.</span> Every order is
                  packed with love from our little studio in Limon, CO.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-24 lg:mt-32" data-testid="categories-section">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="eyebrow mb-3">Wander the garden</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[var(--ink-forest)] tracking-tight">
              Three little corners
            </h2>
          </div>
          <Link
            to="/shop"
            className="text-sm font-medium text-[var(--ink-forest)] hover:text-[var(--terracotta)] underline underline-offset-4"
          >
            See everything →
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-5 lg:gap-7">
          {CATEGORIES.map((cat, i) => (
            <Link
              key={cat.id}
              to={`/shop?category=${cat.id}`}
              className="group relative overflow-hidden rounded-3xl aspect-[3/4] block"
              data-testid={`category-card-${cat.id}`}
              style={{ transform: i === 1 ? "translateY(2rem)" : "" }}
            >
              <img
                src={cat.image}
                alt={cat.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink-forest)] via-[var(--ink-forest)]/60 to-[var(--ink-forest)]/10" />
              <div className="absolute bottom-0 left-0 right-0 p-7">
                <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#F5D78A] mb-2 drop-shadow-md">{cat.blurb}</p>
                <h3 className="font-serif text-3xl text-white drop-shadow-lg">{cat.name}</h3>
                <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-white group-hover:gap-3 transition-all">
                  Browse <ArrowUpRight className="w-4 h-4" />
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* NEW ARRIVALS */}
      {newArrivals.length > 0 && (
        <section
          className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-24 lg:mt-32"
          data-testid="new-arrivals-section"
        >
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="eyebrow mb-3 text-[var(--terracotta)]">Just landed</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[var(--ink-forest)] tracking-tight">
                New this week
              </h2>
              <p className="mt-3 text-[var(--muted-fg)] max-w-xl">
                Fresh additions to the apothecary — the newest bottles, blends, and finds.
              </p>
            </div>
            <Link
              to="/shop"
              className="text-sm font-medium text-[var(--ink-forest)] hover:text-[var(--terracotta)] underline underline-offset-4"
              data-testid="new-arrivals-see-all"
            >
              See everything →
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7">
            {newArrivals.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      {/* FEATURED */}
      <FathersDayPicks featured={featured} loading={loading} />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-32" data-testid="featured-section">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="eyebrow mb-3">Picked with love</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[var(--ink-forest)] tracking-tight">
              This week&apos;s favorites
            </h2>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-square bg-[var(--muted)] rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>

      {/* FEATURED RECIPES */}
      {recipes.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-32" data-testid="featured-recipes-section">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="eyebrow mb-3">Make something tonight</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-[var(--ink-forest)] tracking-tight">
                Free recipes <span className="italic text-[var(--terracotta)]">from our shelf</span>
              </h2>
              <p className="mt-4 text-[var(--muted-fg)] max-w-xl">
                Roller blends, sprays, candles, and diffuser recipes — with one-tap shopping for every ingredient.
              </p>
            </div>
            <Link
              to="/recipes"
              className="text-sm font-medium text-[var(--ink-forest)] hover:text-[var(--terracotta)] underline underline-offset-4"
            >
              All recipes →
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {recipes.map((r) => (
              <RecipeCard key={r.id} recipe={r} />
            ))}
          </div>
        </section>
      )}

      {/* SOURCING — Loved from farm to bottle */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-32" data-testid="sourcing-section">        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-3">Loved from farm to bottle</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[var(--ink-forest)] tracking-tight leading-tight">
              Pure oils,
              <span className="italic text-[var(--terracotta)]"> sourced where they thrive.</span>
            </h2>
            <p className="mt-6 text-[var(--muted-fg)] leading-relaxed text-base">
              Each plant has a home — a climate and soil where it grows truest. Our essential oils
              are gathered from small farms around the world, where lavender meets the south of
              France, eucalyptus belongs to Australia, and frankincense is hand-tapped in Somalia.
            </p>
            <p className="mt-4 text-[var(--muted-fg)] leading-relaxed text-base">
              Every bottle is quality-tested and finished in a small commercial kitchen in the USA —
              honest, traceable, and made to last.
            </p>
            <Link
              to="/about"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[var(--ink-forest)] hover:text-[var(--terracotta)] underline underline-offset-4 decoration-[var(--terracotta)]"
              data-testid="sourcing-cta"
            >
              Read our full story <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
            <SourcePillar
              icon={Handshake}
              title="Direct from the farm"
              body="No brokers, no middlemen — fair prices for the farmer, honest prices for you."
            />
            <SourcePillar
              icon={Beaker}
              title="GC/MS lab tested"
              body="Every batch independently tested for 100% purity before it ever sees a bottle."
              raised
            />
            <SourcePillar
              icon={Globe2}
              title="Bottled in amber glass"
              body="Hand-filled in the USA in deep amber bottles that protect each oil from UV light — kept fresh, kept potent."
            />
          </div>
        </div>
      </section>

      {/* PAWS & HARMONY — sister site */}
      <PawsHarmonyCard />

      {/* STORY */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-32 lg:mt-40">
        <div className="bg-[var(--ink-forest)] text-[var(--bg-cream)] rounded-[2rem] p-10 lg:p-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="eyebrow text-[var(--gold)] mb-4">Our story</p>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight">
              A little shop with a big garden heart.
            </h2>
          </div>
          <div className="text-[var(--bg-cream)]/80 text-base lg:text-lg leading-relaxed space-y-4">
            <p>
              We began collecting jars and bottles long before we had a name for it — apothecary glass
              from estate sales, pressed flowers from grandma&apos;s almanac, gnomes from a roadside
              wonder-shop in Vermont.
            </p>
            <p>
              Now we curate the parts we love most: clean essential oils, real DIY supplies, and
              characters for your garden you&apos;ll want to name.
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 mt-2 bg-[var(--gold)] text-[var(--ink-forest)] rounded-full px-7 py-3 text-sm font-semibold hover:bg-[var(--terracotta)] hover:text-white transition-colors"
              data-testid="story-cta"
            >
              Browse the shop <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div>
      <Icon className="w-5 h-5 text-[var(--terracotta)] mb-2" />
      <p className="text-xs text-[var(--muted-fg)]">{label}</p>
      <p className="text-sm font-semibold text-[var(--ink-forest)]">{value}</p>
    </div>
  );
}

function SourcePillar({ icon: Icon, title, body, raised }) {
  return (
    <div
      className={`bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl p-6 ${
        raised ? "lg:-translate-y-4 shadow-[0_16px_48px_rgba(26,54,38,0.06)]" : ""
      }`}
    >
      <div className="w-10 h-10 rounded-full bg-[var(--terracotta)]/10 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-[var(--terracotta)]" />
      </div>
      <h3 className="font-serif text-xl text-[var(--ink-forest)] mb-2">{title}</h3>
      <p className="text-sm text-[var(--muted-fg)] leading-relaxed">{body}</p>
    </div>
  );
}


// Father's Day weekend picks — auto-hides after the holiday. Pulls from existing
// featured products so admin just needs to flag her favorites.
const FATHERS_DAY_EXPIRY = new Date("2026-06-22T06:59:00Z");

function FathersDayPicks({ featured, loading }) {
  if (new Date() >= FATHERS_DAY_EXPIRY) return null;
  const picks = (featured || []).slice(0, 4);
  if (!loading && picks.length === 0) return null;

  return (
    <section
      className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-24 lg:mt-32"
      data-testid="fathers-day-picks-section"
    >
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1A2B1F] via-[#2E5C44] to-[#3A4F2A] p-8 sm:p-10 lg:p-14 border border-[#D9A85C]/25">
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage:
              "radial-gradient(circle at 10% 20%, #D9A85C 0%, transparent 40%), radial-gradient(circle at 90% 80%, #5C7A4A 0%, transparent 40%)",
          }}
        />
        <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-4">
            <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#D9A85C] mb-3">
              Father&apos;s Day · June 21
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#FFF7E6] tracking-tight leading-[1.05]">
              For the rugged
              <span className="block italic text-[#D9A85C] mt-1">ones we love</span>
            </h2>
            <p className="mt-5 text-[#FFF7E6]/85 leading-relaxed text-base">
              The smell of a workshop, the soft glow of a porch lantern, the man
              who taught you to whittle. Three codes through Sunday — for the
              dads, granddads, and father-figures who deserve something good.
            </p>

            <div className="mt-6 space-y-2 text-sm">
              <PromoLine code="DAD15" blurb="15% off everything" />
              <PromoLine code="GENTLEMAN20" blurb="20% off orders $50+" />
              <PromoLine code="FREESHIPDAD" blurb="Free shipping over $35" />
            </div>

            <Link
              to="/shop"
              className="mt-7 inline-flex items-center gap-2 bg-[#FFF7E6] text-[#1F3A2A] hover:bg-[#D9A85C] rounded-full px-6 py-3 text-sm font-semibold transition-colors"
              data-testid="fathers-day-shop-cta"
            >
              Shop the picks
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="lg:col-span-8">
            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="aspect-square bg-[#FFF7E6]/10 rounded-2xl animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {picks.map((p) => (
                  <FathersDayCard key={p.id} product={p} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function PromoLine({ code, blurb }) {
  return (
    <div className="flex items-center gap-3">
      <span className="font-mono text-xs font-bold tracking-wider text-[#D9A85C] bg-[#FFF7E6]/10 border border-[#D9A85C]/40 rounded-full px-3 py-1">
        {code}
      </span>
      <span className="text-[#FFF7E6]/80">{blurb}</span>
    </div>
  );
}

function FathersDayCard({ product }) {
  return (
    <Link
      to={`/product/${product.id}`}
      className="group block"
      data-testid={`fathers-day-pick-${product.id}`}
    >
      <div className="aspect-square overflow-hidden rounded-2xl bg-[#FFF7E6]/10 border border-[#D9A85C]/20">
        <img
          src={resolveFDImg(product.image_url)}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <p className="mt-2.5 text-xs text-[#D9A85C] truncate font-medium">{product.name}</p>
      <p className="text-[11px] text-[#FFF7E6]/70">${product.price.toFixed(2)}</p>
    </Link>
  );
}

function resolveFDImg(url) {
  if (!url) return "";
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  if (url.startsWith("/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}
